// externals
import type { Reducer } from 'redux';

// libraries
import type { Lender } from '@makemydeal/dr-platform-types';
import type { LenderListState } from '@makemydeal/dr-dash-types';
import { initActionTypes } from '@makemydeal/dr-shared-store';

// consts/enums
import { FETCH_LENDER_LIST_SUCCESS } from '../actionTypes/lenderActionTypes';

// utils
import * as typeGuards from '../utils/typeGuards';

const { INIT_PENCIL_SUCCESS } = initActionTypes;

export type LenderListReducer = Reducer<LenderListState>;

const defaultState: LenderListState = [];

/**
 * Appends the credit decision lender to the lender list if it exists in the offer
 * but is not already present in the lender list.
 */
const appendCreditDecisionLender = (lenderList: Lender[], offer: any): Lender[] => {
    if (!offer) {
        return lenderList;
    }

    // Some external DXG deals send selected lender information that does not exist within the lenderList response
    // In these cases we need to append them to the lenderList so the Lender drop down behaves correctly
    const dealType = offer.type;
    const creditDecisionEnabled = offer[dealType]?.creditDecisionEnabled;
    const creditDecisionLender = offer[dealType]?.creditDecisionLender;
    const isValidLender = typeGuards.isCreditDecisionLender(creditDecisionLender);

    if (!isValidLender) {
        return lenderList;
    }

    const isLenderFromOfferInLenderList = lenderList.some((lender) => lender.lenderId === creditDecisionLender.id);

    if (creditDecisionEnabled && !isLenderFromOfferInLenderList) {
        return [...lenderList, { lenderId: creditDecisionLender.id, lenderName: creditDecisionLender.name } as Lender];
    }

    return lenderList;
};

export const reducer: LenderListReducer = (state: LenderListState = defaultState, action: any) => {
    switch (action.type) {
        case INIT_PENCIL_SUCCESS: {
            if (action.payload.featureToggles?.enableMVDeferredLenderList) {
                // When the deferred lender list toggle is enabled, skip processing in INIT_PENCIL_SUCCESS
                // When the toggle is removed, this case can be deleted
                return state;
            }
            const lenderList = action.payload.lenderList?.lenderList || [];
            return appendCreditDecisionLender(lenderList, action.payload.offer);
        }
        case FETCH_LENDER_LIST_SUCCESS: {
            // The deferred lender list endpoint returns lenderList directly in payload
            const lenderList = action.payload?.lenderList || [];
            return appendCreditDecisionLender(lenderList, action.meta?.offer);
        }
        default: {
            return state;
        }
    }
};
